import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Stack,
  Typography,
  Box,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getCountryFlag } from "@/constants/country-flags";
import { tableCompactSx, tableContainerSx } from "@/components/table/table-styles";
import { tokens } from "@/tokens";
import { formatCurrency, getInitials } from "../utils";
import StatusBadge from "./status-badge";

interface SupplierTableProps {
  suppliers: any[];
  searchQuery: string;
}

export const SupplierTable: React.FC<SupplierTableProps> = ({ suppliers, searchQuery }) => {
  const navigate = useNavigate();

  const filtered = suppliers.filter((s: any) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (s.supplierName || "").toLowerCase().includes(q) ||
      (s.mid || "").toLowerCase().includes(q) ||
      (s.factoryName || "").toLowerCase().includes(q) ||
      (s.country || "").toLowerCase().includes(q) ||
      (s.factoryCountry || "").toLowerCase().includes(q)
    );
  });

  return (
    <Box sx={{ ...tableContainerSx, ...tableCompactSx }}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Middleman</TableCell>
              <TableCell>Factory</TableCell>
              <TableCell align="center">Active Cases</TableCell>
              <TableCell align="right">Est. Savings</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((supplier: any) => {
              const middlemanCountry = supplier.country || (supplier.mid ? supplier.mid.substring(0, 2).toUpperCase() : "");
              return (
                <TableRow
                  key={supplier._id}
                  onClick={() => navigate(`/first-sale/supplier/${supplier._id}`)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: tokens.bg.hover,
                          color: tokens.brand.primary,
                          fontSize: tokens.fontSize.base,
                          fontWeight: tokens.fontWeight.bold,
                          borderRadius: tokens.radius.sm,
                        }}
                      >
                        {getInitials(supplier.supplierName || "")}
                      </Avatar>
                      <Box>
                        <Typography
                          sx={{
                            fontWeight: tokens.fontWeight.bold,
                            fontSize: tokens.fontSize.sm2,
                            color: tokens.text.primary,
                          }}
                        >
                          {supplier.supplierName}
                        </Typography>
                        {middlemanCountry && (
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Typography component="span" sx={{ fontSize: "0.85rem" }}>
                              {getCountryFlag(middlemanCountry)}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: tokens.fontSize["2xs"],
                                color: tokens.text.secondary,
                              }}
                            >
                              {middlemanCountry}
                            </Typography>
                          </Stack>
                        )}
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {supplier.factoryName ? (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {supplier.factoryCountry && (
                          <Typography component="span" sx={{ fontSize: "0.85rem" }}>
                            {getCountryFlag(supplier.factoryCountry)}
                          </Typography>
                        )}
                        <Typography
                          sx={{
                            fontSize: tokens.fontSize.sm2,
                            color: tokens.text.body,
                          }}
                        >
                          {supplier.factoryName}
                        </Typography>
                      </Stack>
                    ) : (
                      <Typography sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.muted }}>
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Typography
                      sx={{
                        fontSize: tokens.fontSize.sm2,
                        fontWeight: (supplier.activeCases || supplier.casesCount || 0) > 0 ? tokens.fontWeight.bold : tokens.fontWeight.normal,
                        color: (supplier.activeCases || supplier.casesCount || 0) > 0 ? tokens.brand.primary : tokens.text.body,
                      }}
                    >
                      {supplier.activeCases || supplier.casesCount || 0}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      sx={{
                        fontSize: tokens.fontSize.sm2,
                        fontWeight: tokens.fontWeight.bold,
                        color: (supplier.totalSavings || 0) > 0 ? tokens.status.success : tokens.text.body,
                      }}
                    >
                      {formatCurrency(supplier.totalSavings || 0)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={supplier.status || "ACTIVE"} />
                  </TableCell>
                </TableRow>
              );
            })}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                  <Typography sx={{ color: tokens.text.secondary, fontSize: tokens.fontSize.sm2 }}>
                    No suppliers found. Click "Add Supplier" to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default SupplierTable;
